/**
 * Line-layout engine for text boxes.
 *
 * Turns (sanitized) rich HTML into positioned text chunks while flowing around
 * obstacles (see textWrap.js). Words are measured with a 2D canvas so the
 * layout can be recomputed on every frame while an object is dragged.
 */
import { blockedIntervals, freeSegments } from './textWrap';
import { trackingToLetterSpacing } from './textUtils';

/* ------------------------------------------------------------------ */
/* Measurement                                                         */
/* ------------------------------------------------------------------ */

let ctx = null;
const getCtx = () => {
  if (!ctx) ctx = document.createElement('canvas').getContext('2d');
  return ctx;
};
const widthCache = new Map();
const MAX_CACHE = 30000;

export const fontString = (s) => `${s.fontStyle === 'italic' ? 'italic ' : ''}${s.smallCaps ? 'small-caps ' : ''}${Number(s.fontWeight) >= 600 || s.fontWeight === 'bold' ? '700' : '400'} ${Math.max(1, s.fontSize)}px '${s.fontFamily || 'Inter'}', sans-serif`;

export const measureWidth = (text, style) => {
  if (!text) return 0;
  const f = fontString(style);
  const key = `${f}|${style.letterSpacing || 0}|${text}`;
  let w = widthCache.get(key);
  if (w === undefined) {
    const c = getCtx();
    c.font = f;
    w = c.measureText(text).width + (style.letterSpacing || 0) * text.length;
    if (widthCache.size > MAX_CACHE) widthCache.clear();
    widthCache.set(key, w);
  }
  return w;
};

export const clearMeasureCache = () => widthCache.clear();

/** Ink extents above/below the baseline (px) for a run of text — used by WordArt to stretch glyphs to the frame. */
export const measureInk = (text, style) => {
  const f = fontString(style);
  const key = `ink|${f}|${text}`;
  let v = widthCache.get(key);
  if (v === undefined) {
    const c = getCtx();
    c.font = f;
    const m = c.measureText(text || 'M');
    const ascent = Number.isFinite(m.actualBoundingBoxAscent) && m.actualBoundingBoxAscent > 0 ? m.actualBoundingBoxAscent : style.fontSize * 0.72;
    const descent = Number.isFinite(m.actualBoundingBoxDescent) && m.actualBoundingBoxDescent >= 0 ? m.actualBoundingBoxDescent : style.fontSize * 0.2;
    v = { ascent, descent };
    if (widthCache.size > MAX_CACHE) widthCache.clear();
    widthCache.set(key, v);
  }
  return v;
};

/* ------------------------------------------------------------------ */
/* Rich text -> paragraphs / runs                                      */
/* ------------------------------------------------------------------ */

const FONT_SIZE_MAP = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 };

const parseLength = (v, base) => {
  if (!v) return undefined;
  const m = String(v).trim().match(/^(-?[\d.]+)(px|pt|em|rem|%)?$/);
  if (!m) return undefined;
  const n = parseFloat(m[1]);
  switch (m[2]) {
    case 'pt': return n * (4 / 3);
    case 'em': case 'rem': return n * base;
    case '%': return (n / 100) * base;
    default: return n;
  }
};

const applyCss = (st, cs) => {
  if (!cs) return st;
  const o = { ...st };
  if (cs.fontFamily) o.fontFamily = cs.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
  const fs = parseLength(cs.fontSize, st.fontSize);
  if (fs) o.fontSize = fs;
  if (cs.fontWeight) o.fontWeight = cs.fontWeight === 'bold' || cs.fontWeight === 'bolder' || parseInt(cs.fontWeight, 10) >= 600 ? 700 : 400;
  if (cs.fontStyle) o.fontStyle = cs.fontStyle;
  if (cs.color) o.color = cs.color;
  const td = cs.textDecorationLine || cs.textDecoration;
  if (td) {
    if (/none/.test(td)) { o.underline = false; o.strike = false; }
    if (/underline/.test(td)) o.underline = true;
    if (/line-through/.test(td)) o.strike = true;
  }
  const ls = parseLength(cs.letterSpacing, st.fontSize);
  if (ls !== undefined) o.letterSpacing = ls;
  if (cs.textTransform) o.textTransform = cs.textTransform;
  if (cs.fontVariant) o.smallCaps = /small-caps/.test(cs.fontVariant);
  if (cs.verticalAlign === 'super') { o.fontSize *= 0.65; o.shift = -0.35; }
  else if (cs.verticalAlign === 'sub') { o.fontSize *= 0.65; o.shift = 0.2; }
  if (cs.backgroundColor) o.background = cs.backgroundColor;
  return o;
};

/**
 * Parse sanitized HTML into paragraphs: [{ runs: [{ text, style }], align?, list?, firstLineIndent?, indentLeft? }]
 */
export const parseRichText = (html, base) => {
  const paragraphs = [];
  let cur = null;
  const ensure = () => { if (!cur) { cur = { runs: [] }; paragraphs.push(cur); } return cur; };
  const endPara = () => { cur = null; };
  const pushText = (text, style) => { if (!text) return; ensure().runs.push({ text, style }); };
  const doc = new DOMParser().parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstChild;
  let counter = 0;

  const walk = (node, style, listType) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const parts = child.nodeValue.split('\n');
        parts.forEach((part, i) => { if (i > 0) endPara(); ensure(); pushText(part, style); });
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      const tag = child.tagName;
      if (tag === 'BR') { ensure(); endPara(); continue; }
      let st = style;
      if (tag === 'B' || tag === 'STRONG') st = { ...st, fontWeight: 700 };
      else if (tag === 'I' || tag === 'EM') st = { ...st, fontStyle: 'italic' };
      else if (tag === 'U') st = { ...st, underline: true };
      else if (tag === 'S' || tag === 'STRIKE') st = { ...st, strike: true };
      else if (tag === 'SUP') st = { ...st, fontSize: st.fontSize * 0.65, shift: -0.35 };
      else if (tag === 'SUB') st = { ...st, fontSize: st.fontSize * 0.65, shift: 0.2 };
      else if (tag === 'A') st = { ...st, underline: true };
      if (tag === 'FONT') {
        const c = child.getAttribute('color'), face = child.getAttribute('face'), size = child.getAttribute('size');
        st = { ...st, ...(c ? { color: c } : null), ...(face ? { fontFamily: face.replace(/['"]/g, '').split(',')[0].trim() } : null), ...(size && FONT_SIZE_MAP[size] ? { fontSize: FONT_SIZE_MAP[size] } : null) };
      }
      if (child.getAttribute('style')) st = applyCss(st, child.style);
      // Character Spacing ▸ Scaling on highlighted text: a `.wp-scale` word is laid out at the scaled size (its advance
      // widths are the scaled widths) and drawn stretched back to full height (LayoutLines: scaleY)
      if (child.classList && child.classList.contains('wp-scale')) {
        const sx = parseFloat(child.style ? child.style.getPropertyValue('--wp-sx') : '');
        if (Number.isFinite(sx) && sx > 0 && sx !== 1) st = { ...st, fontSize: st.fontSize * sx, letterSpacing: st.letterSpacing, stretchY: (st.stretchY || 1) / sx };
      }

      if (tag === 'UL' || tag === 'OL') {
        counter = 0;
        if (cur && cur.runs.length) endPara();
        walk(child, st, tag === 'OL' ? 'number' : 'bullet');
        endPara();
        continue;
      }
      if (tag === 'DIV' || tag === 'P' || tag === 'LI') {
        if (cur && cur.runs.length) endPara();
        const p = ensure();
        if (tag === 'LI') { counter += 1; p.list = { type: listType || 'bullet', index: counter }; }
        const cs = child.style;
        if (cs) {
          if (cs.textAlign) p.align = cs.textAlign;
          // a `text-indent: 0` marks the half of a paragraph that runs on from the previous linked box (storyEdit markers)
          const ti = parseLength(cs.textIndent, st.fontSize); if (ti !== undefined) p.firstLineIndent = ti;
          if (cs.textAlignLast === 'justify') p.justifyLast = true; // the half that breaks off at the bottom of a box
          const ml = parseLength(cs.marginLeft || cs.paddingLeft, st.fontSize); if (ml) p.indentLeft = ml;
          // paragraph-level line spacing (Home ▸ Line Spacing on highlighted paragraphs, imported Publisher paragraphs):
          // unitless = multiplier of the font size; a length is turned into one against the paragraph's base size
          if (cs.lineHeight && cs.lineHeight !== 'normal') {
            // a bare number is a multiple of the font size; a length is an exact line height ("Exactly N pt")
            if (/^[\d.]+$/.test(cs.lineHeight.trim())) { const lh = parseFloat(cs.lineHeight); if (lh > 0) p.lineHeight = lh; }
            else { const px = parseLength(cs.lineHeight, st.fontSize); if (px > 0) p.lineHeightPx = px; }
          }
          const mt = parseLength(cs.marginTop, st.fontSize); if (mt !== undefined) p.spaceBefore = mt;
          const mb = parseLength(cs.marginBottom, st.fontSize); if (mb !== undefined) p.spaceAfter = mb;
          // the paragraph's own font size (a blank line resized from the size box, imported Publisher paragraphs): the
          // height of an empty line and the minimum height of its lines (the browser's strut), like the box's size otherwise
          if (cs.fontSize && st.fontSize) p.fontSize = st.fontSize;
        }
        walk(child, st, listType);
        endPara();
        continue;
      }
      walk(child, st, listType);
    }
  };
  walk(root, base, null);
  if (!paragraphs.length) paragraphs.push({ runs: [] });
  return paragraphs;
};

/* ------------------------------------------------------------------ */
/* Tokenizer                                                           */
/* ------------------------------------------------------------------ */

const transformText = (t, tt) => {
  if (!tt || tt === 'none') return t;
  if (tt === 'uppercase') return t.toUpperCase();
  if (tt === 'lowercase') return t.toLowerCase();
  if (tt === 'capitalize') return t.replace(/(^|\s)(\S)/g, (m, a, b) => a + b.toUpperCase());
  return t;
};

/** Items = words (array of styled parts), glue (whitespace) and tab stops (`tab: true`, width decided at placement). */
const makeItems = (para, fontScale) => {
  const items = [];
  let word = null;
  const flush = () => { if (word) { items.push(word); word = null; } };
  for (const run of para.runs) {
    const style = fontScale !== 1 ? { ...run.style, fontSize: run.style.fontSize * fontScale, letterSpacing: (run.style.letterSpacing || 0) * fontScale } : run.style;
    // split the *untransformed* text so the overflow handed to a linked box keeps the story's own characters (`raw`);
    // text-transform only changes what is measured and drawn
    const rawParts = run.text.split(/( +|\t)/).filter(Boolean);
    for (const rawPart of rawParts) {
      if (rawPart === '\t') {
        flush();
        items.push({ tab: true, glue: false, text: '\t', width: 0, style, parts: [] });
      } else if (/^ +$/.test(rawPart)) {
        flush();
        items.push({ glue: true, text: rawPart, width: measureWidth(rawPart, style), style, parts: [{ text: rawPart, style, width: 0 }] });
      } else {
        const part = transformText(rawPart, style.textTransform);
        const w = measureWidth(part, style);
        if (!word) word = { glue: false, parts: [], width: 0 };
        word.parts.push({ text: part, raw: rawPart, style, width: w });
        word.width += w;
      }
    }
  }
  flush();
  return items;
};

/* ------------------------------------------------------------------ */
/* Tab stops                                                           */
/* ------------------------------------------------------------------ */

export const DEFAULT_TAB_STOP = 48; // px (0.5 in at 96 px/in), Publisher's default
export const TAB_ALIGNMENTS = [['left', 'Left'], ['center', 'Center'], ['right', 'Right'], ['decimal', 'Decimal']];
export const TAB_LEADERS = [['none', 'None'], ['dot', 'Dot'], ['dash', 'Dash'], ['line', 'Line'], ['bullet', 'Bullet']];
const LEADER_CHAR = { dot: '.', dash: '-', line: '_', bullet: '•' };

/** Next tab stop strictly to the right of `x` (column-local px): custom stops first, then the default grid (`def: true`). */
const nextTabStop = (x, stops, def) => {
  for (const s of stops) if (s.pos > x + 0.5) return s;
  const d = def > 1 ? def : DEFAULT_TAB_STOP;
  return { pos: (Math.floor((x + 0.5) / d) + 1) * d, align: 'left', leader: 'none', def: true, interval: d };
};

/** Width of the text that follows a tab up to the next tab / paragraph end; `decimal` = width up to the decimal separator. */
const tabFieldWidth = (its, from, decimal) => {
  let w = 0;
  for (let k = from; k < its.length; k++) {
    const it = its[k];
    if (it.tab) break;
    if (decimal) {
      for (const p of it.glue ? [] : it.parts) {
        const di = p.text.search(/[.,]/);
        if (di >= 0) return w + measureWidth(p.text.slice(0, di), p.style);
        w += p.width;
      }
      if (it.glue) w += it.width;
    } else w += it.width;
  }
  // trailing spaces do not count
  return w;
};

/** Split a word item so that the head fits into `avail` (character level). */
const splitWord = (item, avail) => {
  const head = { glue: false, parts: [], width: 0 };
  const tail = { glue: false, parts: [], width: 0 };
  let broken = false;
  for (const part of item.parts) {
    if (broken) { tail.parts.push(part); tail.width += part.width; continue; }
    if (head.width + part.width <= avail) { head.parts.push(part); head.width += part.width; continue; }
    // break inside this part
    let lo = 0, hi = part.text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (head.width + measureWidth(part.text.slice(0, mid), part.style) <= avail) lo = mid; else hi = mid - 1;
    }
    // the untransformed text splits at the same character when the transform kept the length (always, except for
    // the rare multi-character case folds such as ß → SS)
    const raw = part.raw !== undefined && part.raw.length === part.text.length ? part.raw : null;
    if (lo > 0) {
      const t = part.text.slice(0, lo);
      const w = measureWidth(t, part.style);
      head.parts.push({ text: t, raw: raw ? raw.slice(0, lo) : t, style: part.style, width: w });
      head.width += w;
    }
    const rest = part.text.slice(lo);
    if (rest) { const w = measureWidth(rest, part.style); tail.parts.push({ text: rest, raw: raw ? raw.slice(lo) : rest, style: part.style, width: w }); tail.width += w; }
    broken = true;
  }
  return [head.parts.length ? head : null, tail.parts.length ? tail : null];
};

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

const maxFont = (items) => { let m = 0; for (const it of items) for (const p of it.parts) if (p.style.fontSize > m) m = p.style.fontSize; return m; };

const buildChunks = (seg, align, justify, listMarker) => {
  const chunks = [];
  let x = seg.x;
  const slack = seg.w - seg.used;
  if (align === 'center') x += slack / 2;
  else if (align === 'right') x += slack;
  let extra = 0;
  // Justify, but never stretch a short fragment into a "river" of spaces (Publisher keeps these ragged)
  if (justify && slack > 0 && slack <= seg.w * 0.4) {
    const glues = seg.items.filter((i) => i.glue).length;
    if (glues > 0) extra = slack / glues;
  }
  if (listMarker) chunks.push(listMarker);
  let open = null;
  const closeChunk = () => { if (open) { chunks.push(open); open = null; } };
  for (const it of seg.items) {
    if (it.tab) {
      closeChunk();
      const ch = LEADER_CHAR[it.leader];
      if (ch && it.width > 0) {
        // leader glyphs fill the gap and end at the tab stop
        const cw = measureWidth(ch, it.style) || 1;
        const n = Math.floor((it.width - 1) / cw);
        if (n > 0) chunks.push({ x: x + it.width - n * cw, text: ch.repeat(n), style: it.style, width: n * cw, leader: true });
      }
      x += it.width;
      continue;
    }
    if (it.glue) {
      if (extra > 0) { closeChunk(); x += it.width + extra; continue; }
      if (open && open.style === it.style) { open.text += it.text; open.width += it.width; } else { closeChunk(); open = { x, text: it.text, style: it.style, width: it.width }; }
      x += it.width;
      continue;
    }
    for (const p of it.parts) {
      if (open && open.style === p.style) { open.text += p.text; open.width += p.width; }
      else { closeChunk(); open = { x, text: p.text, style: p.style, width: p.width }; }
      x += p.width;
    }
  }
  closeChunk();
  return chunks.filter((c) => c.text.trim().length || c.marker);
};

/**
 * Lay out paragraphs inside width x height, flowing around obstacles.
 * Returns { lines: [{ y, h, chunks: [{ x, text, style, width }] }], overflowText, fits, contentHeight }
 */
export const layoutText = ({ paragraphs, width, height, obstacles = [], base, fontScale = 1 }) => {
  const cols = Math.max(1, Math.floor(base.columns || 1));
  const gap = cols > 1 ? base.columnGap || 16 : 0;
  const colW = Math.max(1, (width - gap * (cols - 1)) / cols);
  // the box's line spacing: a multiple, or an exact height in px ("16px" = Exactly 12 pt — not scaled with the text)
  const baseLhPx = typeof base.lineHeight === 'string' && /px$/.test(base.lineHeight) ? parseFloat(base.lineHeight) : undefined;
  const baseLh = baseLhPx === undefined && base.lineHeight ? Number(base.lineHeight) || 1.2 : 1.2;
  const baseFs = (base.fontSize || 16) * fontScale;
  const minSeg = Math.max(16, baseFs * 1.5);
  const paras = paragraphs.map((p) => ({ para: p, items: makeItems(p, fontScale) }));
  const stops = (base.tabStops || []).filter((s) => Number.isFinite(s.pos)).slice().sort((a, b) => a.pos - b.pos);
  const defTab = base.defaultTabStop || DEFAULT_TAB_STOP;
  const grid = base.baseline && base.baseline.spacing > 1 ? base.baseline : null;
  /** Align to Baselines: push the line down so its baseline sits on the next baseline guide. */
  const snap = (y, lh, fs) => {
    if (!grid) return y;
    const bo = lh / 2 + 0.3 * fs; // baseline inside the line box (ascent ≈ 0.8em, descent ≈ 0.2em, glyphs centred)
    const abs = (grid.origin || 0) + y + bo;
    const target = grid.offset + Math.ceil((abs - grid.offset - 0.01) / grid.spacing) * grid.spacing;
    return y + (target - abs);
  };

  const run = (startY) => {
    const lines = [];
    let col = 0;
    let y = startY;
    let bottom = startY;
    let overflow = null; // { pi, ii }
    let pi = 0;
    let guard = 0;
    while (pi < paras.length && guard++ < 20000) {
      const { para } = paras[pi];
      const its = paras[pi].items.map((i) => i); // shallow copy so splits don't leak into other passes
      let ii = 0;
      let first = true;
      // a paragraph's own line spacing / space before / after (inline styles) win over the box's
      const lhMult = para.lineHeight || baseLh;
      // an exact height wins over multiples: the paragraph's own, else the box's (unless the paragraph set a multiple); autofit scales it like the text
      const exactLhRaw = para.lineHeightPx ?? (para.lineHeight ? undefined : baseLhPx);
      const exactLh = exactLhRaw === undefined ? undefined : exactLhRaw * fontScale;
      const paraFs = para.fontSize ? para.fontSize * fontScale : baseFs; // the paragraph's own size (blank lines), else the box's
      const spaceAfter = para.spaceAfter ?? base.spaceAfter ?? 0;
      y += para.spaceBefore ?? base.spaceBefore ?? 0;
      const listIndent = para.list ? baseFs * 1.6 : 0;
      const align = para.align || base.textAlign || 'left';
      // empty paragraph -> one blank line
      if (!its.length) {
        const lh = exactLh ?? paraFs * lhMult;
        const ys = snap(y, lh, paraFs);
        if (ys + lh > height + 0.5) { if (col < cols - 1) { col++; y = startY; } else { overflow = { pi, ii: 0, its }; break; } }
        lines.push({ y: ys, h: lh, chunks: [] });
        y = ys + lh; bottom = y; y += spaceAfter; pi++; continue;
      }
      let paraDone = false;
      while (!paraDone && guard++ < 20000) {
        const lhBase = exactLh ?? Math.max(paraFs, maxFont(its.slice(ii, ii + 40)) || paraFs) * lhMult;
        if (snap(y, lhBase, paraFs) + lhBase > height + 0.5) {
          if (col < cols - 1) { col++; y = startY; continue; }
          overflow = { pi, ii, its }; break; // `its` = this paragraph's item list as laid out (hyphenated words were split in place)
        }
        const xcol = col * (colW + gap);
        const left = (base.indentLeft || 0) + (para.indentLeft || 0) + listIndent + (first ? (para.firstLineIndent ?? base.firstLineIndent ?? 0) : 0);
        const right = colW - (base.indentRight || 0);
        const blocked = [];
        for (const ob of obstacles) for (const iv of blockedIntervals(ob, y, y + lhBase, width)) blocked.push([iv[0] - xcol, iv[1] - xcol]);
        const fullFree = blocked.length === 0;
        const segs = freeSegments(left, right, blocked, minSeg);
        if (!segs.length) { y += lhBase; continue; }

        const lineSegs = [];
        let placedAny = false;
        for (let s = 0; s < segs.length && ii < its.length; s++) {
          const [sx, ex] = segs[s];
          const segW = ex - sx;
          let used = 0;
          const placed = [];
          while (ii < its.length && its[ii].glue) ii++; // collapse leading spaces
          while (ii < its.length) {
            const it = its[ii];
            if (it.tab) {
              // Tab: jump to the next stop (custom stops, then the default grid); centre / right / decimal stops
              // position the following field around the stop, like Publisher. Same rules as lib/tabLayout.js: a default
              // stop only counts while a whole default interval is left after it, and a tab sticks to the word after it.
              const cur = sx + used;
              const stop = nextTabStop(cur, stops, defTab);
              const noStop = stop.pos > ex + 0.5 || (stop.def && placed.some((p) => !p.glue) && stop.pos + stop.interval > ex + 0.5);
              if (noStop) {
                if (placed.some((p) => !p.glue)) break; // no room before the segment ends → wrap, tab continues on the next line
                ii++; continue; // nothing on the line yet: the stop lies beyond this segment, drop the tab
              }
              let w = stop.pos - cur;
              let fitted = false; // an aligned field positioned around its stop fits by construction
              if (stop.align === 'right') w -= tabFieldWidth(its, ii + 1, false);
              else if (stop.align === 'center') w -= tabFieldWidth(its, ii + 1, false) / 2;
              else if (stop.align === 'decimal') w -= tabFieldWidth(its, ii + 1, true);
              if (w < 0) w = stop.pos - cur; // field too wide for an aligned stop → behaves like a left stop
              else if (stop.align && stop.align !== 'left') fitted = true;
              // the word after the tab must fit too — otherwise the line breaks before the tab, which lands on the
              // first stop of the next line
              const nxt = its[ii + 1];
              if (!fitted && nxt && !nxt.glue && !nxt.tab && used + w + nxt.width > segW + 0.01 && placed.some((p) => !p.glue)) break;
              placed.push({ ...it, width: w, leader: stop.leader || 'none' });
              used += w; ii++; placedAny = true;
              continue;
            }
            if (it.glue) {
              if (used + it.width <= segW + 0.01) { placed.push(it); used += it.width; ii++; continue; }
              ii++; break; // space at wrap point is dropped
            }
            if (used + it.width <= segW + 0.01) { placed.push(it); used += it.width; ii++; placedAny = true; continue; }
            // word does not fit
            const empty = placed.every((p) => p.glue || p.tab);
            const lastChance = s === segs.length - 1 && !placedAny;
            if (empty && (segW >= colW * 0.6 || it.width > colW * 0.9 || (lastChance && fullFree))) {
              const [head, tail] = splitWord(it, segW - used);
              if (head) {
                placed.push(head); used += head.width; placedAny = true;
                if (tail) its.splice(ii, 1, tail); else ii++;
              }
            }
            break;
          }
          while (placed.length && placed[placed.length - 1].glue) used -= placed.pop().width;
          if (placed.length) lineSegs.push({ x: sx + xcol, w: segW, used, items: placed, endsPara: ii >= its.length });
        }
        if (!placedAny) {
          if (fullFree && ii < its.length) {
            // nothing fits even on a free line (e.g. a huge glyph): force one char
            const it = its[ii];
            const [head, tail] = splitWord(it, Infinity);
            if (head) { lineSegs.push({ x: segs[0][0] + xcol, w: segs[0][1] - segs[0][0], used: head.width, items: [head], endsPara: !tail && ii + 1 >= its.length }); if (tail) its.splice(ii, 1, tail); else ii++; }
            else ii++;
          } else { y += lhBase; continue; }
        }
        const lh = exactLh ?? Math.max(paraFs * lhMult * 0.5, (maxFont(lineSegs.flatMap((sg) => sg.items)) || paraFs) * lhMult);
        const ys = snap(y, lh, maxFont(lineSegs.flatMap((sg) => sg.items)) || baseFs);
        const marker = first && para.list ? { marker: true, x: left - listIndent + xcol, text: para.list.type === 'number' ? `${para.list.index}.` : '\u2022', style: its[0]?.parts?.[0]?.style || { fontSize: baseFs, fontFamily: base.fontFamily }, width: listIndent } : null;
        const chunks = [];
        lineSegs.forEach((sg, idx) => {
          const justify = align === 'justify' && (!sg.endsPara || !!para.justifyLast);
          chunks.push(...buildChunks(sg, align === 'justify' ? 'left' : align, justify, idx === 0 ? marker : null));
        });
        lines.push({ y: ys, h: lh, chunks });
        y = ys + lh; bottom = y;
        first = false;
        if (ii >= its.length) paraDone = true;
      }
      if (overflow) break;
      y += spaceAfter;
      pi++;
    }
    // overflow text (plain) for linked text boxes
    // (the paragraph the overflow starts in is read from the list the layout actually used — a hyphenated word split at
    // the bottom of the box lives there as two items — and from the untransformed text, so the overflow is an exact
    // suffix of the story's plain text, which the linked-box display relies on)
    let overflowText = '';
    if (overflow) {
      const parts = [];
      for (let p = overflow.pi; p < paras.length; p++) {
        const its = p === overflow.pi && overflow.its ? overflow.its : paras[p].items;
        const from = p === overflow.pi ? overflow.ii : 0;
        parts.push(its.slice(from).map((it) => (it.tab ? '\t' : it.glue ? it.text : it.parts.map((q) => q.raw ?? q.text).join(''))).join('').replace(/^\s+/, ''));
      }
      overflowText = parts.join('\n');
    }
    return { lines, overflowText, fits: !overflow, contentHeight: bottom - startY };
  };

  let result = run(0);
  const va = base.verticalAlign || 'top';
  if (cols === 1 && va !== 'top' && result.fits && result.contentHeight < height) {
    const off = va === 'middle' ? (height - result.contentHeight) / 2 : height - result.contentHeight;
    const second = run(off);
    if (second.fits) result = second;
  }
  return result;
};

/* ------------------------------------------------------------------ */
/* High level helper                                                   */
/* ------------------------------------------------------------------ */

/** Text-box props the engine cannot reproduce -> fall back to normal HTML rendering. */
export const isEngineEligible = (p = {}) => !p.dropCap && !(p.textScale && p.textScale !== 100);

/**
 * Props that can only be honoured by the engine (so it runs even without wrap obstacles). Custom tab stops are not
 * among them any more: the browser rendering honours them through measured tab spans (lib/tabLayout.js), identically
 * while editing and on display.
 */
export const needsEngine = (p = {}) => !!p.alignToBaselines || p.textDirection === 'vertical';

/** Publication baseline grid (Layout Guides ▸ Baseline Guides); kept in sync from the editor state so exports match. */
export const baselineGrid = { spacing: 14, offset: 0 };
export const setBaselineGrid = (g) => { if (g) { baselineGrid.spacing = Math.max(2, Number(g.spacing) || 14); baselineGrid.offset = Number(g.offset) || 0; } };
/** The grid is saved with the publication as a special guide entry { id: 'baseline', kind: 'baseline', spacing, offset }. */
export const BASELINE_GUIDE_ID = 'baseline';
export const getBaselineGuide = (guides = [], fallbackSpacing = 14) => {
  const g = (guides || []).find((x) => x && (x.id === BASELINE_GUIDE_ID || x.kind === 'baseline'));
  return { spacing: Math.max(2, Number(g?.spacing) || fallbackSpacing || 14), offset: Number(g?.offset) || 0 };
};

/**
 * "Rotate all text 90°": lay the text out in a frame whose axes are swapped (width = box height) and rotate the result
 * clockwise. Obstacles given in box space (x right, y down) map to the frame as (x, y) → (y, W − x).
 */
export const isVerticalText = (p = {}) => p.textDirection === 'vertical';
export const rotateObstaclesForVertical = (obstacles, W) => obstacles.map((ob) => {
  const poly = ob.poly.map(([x, y]) => [y, W - x]);
  const b = ob.bbox;
  return { ...ob, poly, bbox: { x0: b.y0, y0: W - b.x1, x1: b.y1, y1: W - b.x0 } };
});

export const baseStyleFromProps = (p) => {
  const fontSize = p.fontSize || 16;
  return {
    fontFamily: p.fontFamily || 'Inter',
    fontSize,
    fontWeight: p.fontWeight === 'bold' || p.fontWeight === '700' || Number(p.fontWeight) >= 600 ? 700 : 400,
    fontStyle: p.fontStyle || 'normal',
    color: p.textFill?.type === 'solid' && p.textFill.color ? p.textFill.color : p.color || '#000000',
    underline: /underline/.test(p.textDecoration || ''),
    strike: /line-through/.test(p.textDecoration || ''),
    letterSpacing: (p.letterSpacing || 0) + trackingToLetterSpacing(p.tracking || 100, fontSize),
    textTransform: p.textTransform,
    smallCaps: p.fontVariant === 'small-caps',
  };
};

/**
 * Lay out a text box. `props` must already be merged with DEFAULT_TEXT_PROPS.
 * `html` is the display HTML (sanitized) of the box. `contentW/H` are the box's padded content size in box space;
 * vertical text is laid out in the rotated frame automatically. `originY` = page y of the content top (baseline grid).
 */
export const layoutTextBox = (props, html, contentW, contentH, obstacles, { fontScale = 1, originY = 0 } = {}) => {
  const paragraphs = parseRichText(html, baseStyleFromProps(props));
  const vertical = isVerticalText(props);
  const width = Math.max(1, vertical ? contentH : contentW);
  const height = Math.max(1, vertical ? contentW : contentH);
  const obs = vertical ? rotateObstaclesForVertical(obstacles || [], contentW) : obstacles || [];
  return layoutText({
    paragraphs,
    width,
    height,
    obstacles: obs,
    fontScale,
    base: {
      fontFamily: props.fontFamily || 'Inter',
      fontSize: props.fontSize || 16,
      lineHeight: props.lineHeight || 1.2,
      textAlign: props.textAlign || 'left',
      firstLineIndent: props.firstLineIndent || 0,
      indentLeft: props.indentLeft || 0,
      indentRight: props.indentRight || 0,
      spaceBefore: props.spaceBefore || 0,
      spaceAfter: props.spaceAfter || 0,
      columns: props.columns || 1,
      columnGap: props.columnGap || 16,
      verticalAlign: props.verticalAlign || 'top',
      tabStops: props.tabStops || [],
      defaultTabStop: props.defaultTabStop || DEFAULT_TAB_STOP,
      baseline: props.alignToBaselines && !vertical ? { spacing: baselineGrid.spacing, offset: baselineGrid.offset, origin: originY } : null,
    },
  });
};
